export const MIN_ARTWORK_QUANTITY = 1
export const MAX_ARTWORK_QUANTITY = 99
export const DEFAULT_ARTWORK_DPI = 300

function positiveNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

function clampArtworkQuantity(value) {
  return Math.min(
    MAX_ARTWORK_QUANTITY,
    Math.max(MIN_ARTWORK_QUANTITY, parseInt(value, 10) || MIN_ARTWORK_QUANTITY)
  )
}

function normalizeBaseArtworkConfig(config = {}, fallback = {}) {
  const values = config || {}
  const defaults = fallback || {}

  return {
    printWidthIn: values.printWidthIn ?? defaults.printWidthIn,
    printHeightIn: values.printHeightIn ?? defaults.printHeightIn,
    quantity: clampArtworkQuantity(values.quantity ?? defaults.quantity),
    included: (values.included ?? defaults.included) !== false,
  }
}

export function normalizeArtworkConfig(config = {}, fallback = {}) {
  const values = config || {}
  const defaults = fallback || {}
  const normalized = normalizeBaseArtworkConfig(values, defaults)
  const rawVariants = Array.isArray(values.sizeVariants)
    ? values.sizeVariants
    : Array.isArray(defaults.sizeVariants) ? defaults.sizeVariants : null

  if (rawVariants) {
    normalized.sizeVariants = rawVariants.map((variant, index) => {
      const normalizedVariant = normalizeBaseArtworkConfig(variant, normalized)
      return {
        id: String(variant?.id || `size-${index + 2}`),
        ...normalizedVariant,
        ...(Object.prototype.hasOwnProperty.call(variant || {}, 'printWidthIn')
          ? { printWidthIn: variant.printWidthIn }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(variant || {}, 'printHeightIn')
          ? { printHeightIn: variant.printHeightIn }
          : {}),
        included: true,
      }
    })
  }

  return normalized
}

export function artworkSizeGroups(config = {}, fallback = {}) {
  const normalized = normalizeArtworkConfig(config, fallback)

  return [
    { id: 'primary', ...normalizeBaseArtworkConfig(normalized, fallback) },
    ...(normalized.sizeVariants || []).map(variant => ({ ...variant })),
  ]
}

export function nextArtworkSizeVariantId(config = {}) {
  const ids = new Set((config.sizeVariants || []).map(variant => String(variant.id)))
  let number = 2
  while (ids.has(`size-${number}`)) number += 1
  return `size-${number}`
}

export function updateArtworkSizeGroup(config, groupIndex, changes, fallback = {}) {
  const normalized = normalizeArtworkConfig(config, fallback)
  if (groupIndex === 0) return normalizeArtworkConfig({ ...normalized, ...changes }, fallback)

  const variants = [...(normalized.sizeVariants || [])]
  if (!variants[groupIndex - 1]) return normalized
  variants[groupIndex - 1] = { ...variants[groupIndex - 1], ...changes, included: true }
  return normalizeArtworkConfig({ ...normalized, sizeVariants: variants }, fallback)
}

export function expandArtworkSources(images = [], imageConfigs = {}) {
  return images.flatMap(image => {
    const config = normalizeArtworkConfig(imageConfigs?.[image.id], image)

    return artworkSizeGroups(config, image).map(group => {
      const { id: sizeGroupId, ...groupConfig } = group
      return {
        ...image,
        ...groupConfig,
        included: config.included,
        sizeGroupId,
        sizeVariants: [],
      }
    })
  })
}

export function hasMultipleArtworkSizeGroups(sources = []) {
  const seenIds = new Set()
  return sources.some(source => {
    const id = String(source.id)
    if (seenIds.has(id)) return true
    seenIds.add(id)
    return false
  })
}

export function deriveDefaultArtworkDimensions(source = {}, defaultDpi = DEFAULT_ARTWORK_DPI) {
  const processed = source.processingStatus === 'processed' || source.processingStatus === undefined
  const widthPx = positiveNumber(source.widthPx)
  const heightPx = positiveNumber(source.heightPx)

  if (
    !processed ||
    source.printWidthIn != null ||
    source.printHeightIn != null ||
    !widthPx ||
    !heightPx
  ) return null

  const dpi = positiveNumber(source.dpi) || positiveNumber(defaultDpi) || DEFAULT_ARTWORK_DPI

  return {
    printWidthIn: Number((widthPx / dpi).toFixed(2)),
    printHeightIn: Number((heightPx / dpi).toFixed(2)),
  }
}

export function effectiveRequestedQuantity(config, fallback) {
  const normalized = normalizeArtworkConfig(config, fallback)
  if (!normalized.included) return 0

  return artworkSizeGroups(normalized, fallback).reduce(
    (total, group) => total + group.quantity,
    0
  )
}

function withRequestedTotal(config, requestedTotal, fallback) {
  const normalized = normalizeArtworkConfig(config, fallback)
  const hasVariants = Array.isArray(normalized.sizeVariants)
  const variants = (normalized.sizeVariants || []).map(variant => ({ ...variant }))
  const groups = [normalized, ...variants]
  const minimum = groups.length
  const maximum = groups.length * MAX_ARTWORK_QUANTITY
  let delta = Math.min(maximum, Math.max(minimum, requestedTotal)) -
    groups.reduce((total, group) => total + group.quantity, 0)

  const adjustmentOrder = delta >= 0 ? groups : [...groups].reverse()
  adjustmentOrder.forEach(group => {
    if (delta === 0) return
    const available = delta > 0
      ? MAX_ARTWORK_QUANTITY - group.quantity
      : group.quantity - MIN_ARTWORK_QUANTITY
    const adjustment = Math.min(Math.abs(delta), available) * Math.sign(delta)
    group.quantity += adjustment
    delta -= adjustment
  })

  const adjusted = {
    ...normalized,
    quantity: groups[0].quantity,
    ...(hasVariants ? { sizeVariants: variants } : {}),
  }
  return normalizeArtworkConfig(adjusted, fallback)
}

export function countPlacedCopies(placements, uploadedImageId) {
  if (!Array.isArray(placements)) return 0

  return placements.filter(placement => (
    placement?.uploadedImageId != null &&
    String(placement.uploadedImageId) === String(uploadedImageId)
  )).length
}

export function filterLayoutToIncludedSources(layout, sources = []) {
  if (!layout || !Array.isArray(layout.sheets)) return layout

  const inclusionBySourceId = new Map(
    (sources || [])
      .filter(source => source?.id != null)
      .map(source => [String(source.id), normalizeArtworkConfig(source).included])
  )

  return {
    ...layout,
    sheets: layout.sheets.map(sheet => {
      if (!Array.isArray(sheet?.items)) return sheet

      return {
        ...sheet,
        items: sheet.items.filter(item => {
          const sourceId = item?.uploaded_image_id ?? item?.uploadedImageId
          if (sourceId == null) return true

          return inclusionBySourceId.get(String(sourceId)) !== false
        }),
      }
    }),
  }
}

export function reconcileRequestedQuantityAfterAdd(config, placedCount, fallback) {
  const normalized = normalizeArtworkConfig(config, fallback)
  const requested = effectiveRequestedQuantity(normalized, fallback)
  const placedAfterClone = Math.max(0, placedCount || 0) + 1

  return {
    ...withRequestedTotal(normalized, Math.max(requested, placedAfterClone), fallback),
    included: true,
  }
}

export function reconcileRequestedQuantityAfterRemove(config, placedCount, fallback, removedCount = 1) {
  const normalized = normalizeArtworkConfig(config, fallback)
  const requested = effectiveRequestedQuantity(normalized, fallback)
  const removalCount = Math.max(0, removedCount || 0)
  const remainingPlacedCount = Math.max(0, (placedCount || 0) - removalCount)

  if (remainingPlacedCount === 0) {
    return { ...normalized, included: false }
  }

  return {
    ...withRequestedTotal(
      normalized,
      Math.max(remainingPlacedCount, requested - removalCount),
      fallback
    ),
    included: true,
  }
}

export function reconcileRequestedQuantityAfterPlacement(config, placedCount, fallback) {
  const normalized = normalizeArtworkConfig(config, fallback)
  const requested = effectiveRequestedQuantity(normalized, fallback)

  if (requested === 0) {
    return { ...normalized, quantity: MIN_ARTWORK_QUANTITY, included: true }
  }
  if ((placedCount || 0) < requested) {
    return { ...normalized, included: true }
  }

  return {
    ...withRequestedTotal(normalized, (placedCount || 0) + 1, fallback),
    included: true,
  }
}

export function planArtworkPlacements(
  artworkSources,
  placedObjects,
  createInstanceId
) {
  const objectsByImageId = new Map()
  for (const object of placedObjects || []) {
    if (object?.uploadedImageId == null) continue
    const imageId = String(object.uploadedImageId)
    const objects = objectsByImageId.get(imageId) || []
    objects.push(object)
    objectsByImageId.set(imageId, objects)
  }

  const retained = []
  const missing = []
  const removals = []
  const knownImageIds = new Set()

  for (const source of artworkSources || []) {
    const normalizedSource = { ...source, ...normalizeArtworkConfig(source) }
    const imageId = String(normalizedSource.id)
    knownImageIds.add(imageId)
    const existing = objectsByImageId.get(imageId) || []
    const requested = normalizedSource.included ? normalizedSource.quantity : 0
    const retainedObjects = existing.slice(0, requested)
    const surplusObjects = existing.slice(requested)

    retainedObjects.forEach(object => {
      retained.push({
        source: normalizedSource,
        object,
        instanceId: object.objectId || createInstanceId(),
      })
    })
    const missingCount = Math.max(0, requested - retainedObjects.length)
    for (let index = 0; index < missingCount; index += 1) {
      missing.push({ source: normalizedSource, instanceId: createInstanceId() })
    }
    surplusObjects.forEach(object => {
      removals.push({
        source: normalizedSource,
        object,
        reason: requested === 0 ? 'excluded' : 'surplus',
      })
    })
  }

  const orphaned = []
  objectsByImageId.forEach((objects, imageId) => {
    if (!knownImageIds.has(imageId)) orphaned.push(...objects)
  })

  return { retained, missing, removals, orphaned }
}
